import {
  Block,
  Cancel,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Pause,
  Person,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  capitalize,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import type { Task, Team } from "../../types/chat";
import { subString } from "../../utils";

interface TeamStatusProps {
  team: Team;
  tasks?: Task[];
}

export const TeamStatus: React.FC<TeamStatusProps> = ({ team, tasks = [] }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "idle":
        return <CheckCircle color="success" fontSize="small" />;
      case "working":
        return <CircularProgress size={14} color="primary" />;
      case "blocked":
        return <Block color="error" fontSize="small" />;
      case "paused":
        return <Pause color="warning" fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "idle":
        return "success";
      case "working":
        return "primary";
      case "blocked":
        return "error";
      case "paused":
      case "stopped":
        return "warning";
      default:
        return "default";
    }
  };

  const getTaskIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle color="success" fontSize="small" />;
      case "failed":
        return <ErrorIcon color="error" fontSize="small" />;
      case "in_progress":
        return <CircularProgress size={14} color="primary" />;
      case "cancelled":
      case "stopped":
        return <Cancel color="warning" fontSize="small" />;
      case "pending":
        return <CircularProgress size={14} color="primary" />;
      default:
        return <HourglassEmpty fontSize="small" />;
    }
  };

  const getMemberTasks = (memberId: string) => {
    return tasks.filter((task) => task.owner_id === memberId);
  };

  const hasPendingDoneTask = (memberId: string) => {
    return tasks.some(
      (task) => task.owner_id === memberId && task.type === "done" && task.status === "pending",
    );
  };

  // Sort members: pending done tasks first, then working, then others
  // Using toSorted() for immutability (React best practice: preserves original array)
  const sortedMembers = team.members.toSorted((a, b) => {
    const aPendingDone = hasPendingDoneTask(a.id);
    const bPendingDone = hasPendingDoneTask(b.id);

    // Pending done tasks go to the top
    if (aPendingDone && !bPendingDone) return -1;
    if (!aPendingDone && bPendingDone) return 1;

    // If both or neither have pending done tasks, sort by working status
    if (a.state.status === "working" && b.state.status !== "working") return -1;
    if (a.state.status !== "working" && b.state.status === "working") return 1;

    return 0;
  });

  return (
    <Stack spacing={2}>
      {sortedMembers.map((member) => {
        const memberTasks = getMemberTasks(member.id);
        const hasPendingDone = hasPendingDoneTask(member.id);

        return (
          <Card
            key={member.id}
            variant="outlined"
            sx={{
              width: "100%",
              ...(hasPendingDone && {
                borderColor: "success.main",
                borderWidth: 2,
                backgroundColor: "success.50",
              }),
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {member.name}
                    </Typography>
                    {member.role && (
                      <Typography variant="body2" color="text.secondary">
                        ({member.role})
                      </Typography>
                    )}
                  </Stack>
                  <Chip
                    icon={getStatusIcon(member.state.status)}
                    label={capitalize(member.state.status)}
                    size="small"
                    color={getStatusColor(member.state.status)}
                  />
                </Stack>

                {member.state.available_task_types.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      What can they do?
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {member.state.available_task_types.map((taskType) => (
                        <Chip
                          key={taskType}
                          label={capitalize(taskType.replace(/_/g, " "))}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {memberTasks.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Their tasks ({memberTasks.length})
                    </Typography>
                    <Stack direction="column" spacing={1} divider={<Divider />} mt={1}>
                      {memberTasks
                        .toSorted((a, b) => b.added_at - a.added_at)
                        .slice(0, 5)
                        .map((task) => (
                          <Stack direction="row" alignItems="center" spacing={1} key={task.id}>
                            <Box
                              width={24}
                              height={24}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              {getTaskIcon(task.status)}
                            </Box>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              justifyContent="space-between"
                            >
                              <Stack direction="column" alignItems="flex-start" spacing={0.2}>
                                <Typography variant="caption">
                                  {capitalize(
                                    task.input.delegated_task_name?.replace(/_/g, " ") ?? "-",
                                  )}
                                </Typography>
                                <Tooltip title={task.input.task_description ?? "-"}>
                                  <Typography variant="caption" color="text.secondary" ml={1}>
                                    {subString(task.input.task_description ?? "-", 96)}
                                  </Typography>
                                </Tooltip>
                                <Typography variant="caption" color="text.secondary" ml={1}>
                                  at step {task.added_at}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        ))}
                      {memberTasks.length > 5 && (
                        <Typography variant="caption" color="text.secondary">
                          +{memberTasks.length - 5} more tasks...
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                {member.state.questions.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="error">
                      Pending Questions: {member.state.questions.length}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};
